import {
    Car,
    Wrench,
    Warehouse,
    Droplets,
    Umbrella,
    Factory,
    ArrowUpRight,
} from "lucide-react";
import { Reveal } from "./Reveal";
import { scrollToSection } from "../lib/scroll";

const Card = ({ icon: Icon, title, items, featured = false, badge, span, testid, delay, target = "contatti" }) => (
    <Reveal delay={delay} className={span}>
        <article
            data-testid={testid}
            onClick={() => scrollToSection(target)}
            className={`group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-3xl border p-8 transition-[box-shadow,transform,border-color] duration-500 hover:-translate-y-1.5 ${
                featured
                    ? "border-[#E30613]/30 bg-[#0B1F3A] text-white shadow-[0_24px_60px_-16px_rgba(11,31,58,0.4)] hover:shadow-[0_32px_80px_-16px_rgba(11,31,58,0.5)]"
                    : "border-slate-200 bg-white shadow-[0_10px_30px_-5px_rgba(11,31,58,0.08)] hover:border-[#0B1F3A]/20 hover:shadow-[0_24px_48px_-12px_rgba(11,31,58,0.16)]"
            }`}
        >
            {badge && (
                <span
                    data-testid={`${testid}-badge`}
                    className="absolute right-6 top-6 rounded-full bg-[#E30613] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white"
                >
                    {badge}
                </span>
            )}
            <div
                className={`flex h-14 w-14 items-center justify-center rounded-2xl transition-transform duration-500 group-hover:scale-110 ${
                    featured ? "bg-[#E30613]" : "bg-[#0B1F3A]/[0.06]"
                }`}
            >
                <Icon
                    className={`h-7 w-7 ${featured ? "text-white" : "text-[#0B1F3A]"}`}
                    strokeWidth={1.8}
                />
            </div>
            <h3
                className={`font-display mt-6 text-xl font-bold sm:text-2xl ${
                    featured ? "text-white" : "text-[#0B1F3A]"
                }`}
            >
                {title}
            </h3>
            <ul
                className={`mt-4 flex-1 space-y-2 text-sm leading-relaxed ${
                    featured ? "text-white/75" : "text-[#4F5D70]"
                }`}
            >
                {items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5">
                        <span
                            className={`mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full ${
                                featured ? "bg-[#E30613]" : "bg-[#0B1F3A]/30"
                            }`}
                        />
                        {it}
                    </li>
                ))}
            </ul>
            <div
                className={`mt-6 flex items-center gap-2 text-sm font-bold ${
                    featured ? "text-white" : "text-[#0B1F3A]"
                }`}
            >
                Richiedi informazioni
                <ArrowUpRight className="h-4 w-4 text-[#E30613] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </div>
            {featured && (
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-[#E30613]/15 blur-3xl"
                />
            )}
        </article>
    </Reveal>
);

export const Services = () => (
    <section id="servizi" data-testid="servizi-section" className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
            <div className="max-w-2xl">
                <Reveal>
                    <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]">
                        Tutte le divisioni
                    </span>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-[#0B1F3A] sm:text-4xl">
                        Sei divisioni, un unico interlocutore tecnico.
                    </h2>
                </Reveal>
                <Reveal delay={0.18}>
                    <p className="mt-5 text-base leading-relaxed text-[#475569] sm:text-lg">
                        Due anime, una sola squadra: gli{" "}
                        <strong className="font-semibold text-[#0B1F3A]">autolavaggi Christ</strong>{" "}
                        e l'<strong className="font-semibold text-[#0B1F3A]">officina completa</strong>{" "}
                        per l'autoriparatore, affiancate da scaffalature, depurazione acque,
                        coperture e impianti industriali. Forniamo, installiamo e assistiamo con
                        tecnici nostri, in tutto il Sud Italia.
                    </p>
                </Reveal>
            </div>

            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12">
                <Card
                    featured
                    badge="Concessionario Ufficiale"
                    span="lg:col-span-6"
                    target="christ"
                    testid="service-card-autolavaggi"
                    icon={Car}
                    title="Autolavaggi Christ Wash Systems"
                    items={[
                        "Portali e tunnel di lavaggio per auto",
                        "Lavaggio mezzi pesanti, camion e autobus",
                        "Impianti self-service chiavi in mano",
                        "Assistenza tecnica e ricambi originali",
                        "Concessionario ufficiale per la Calabria",
                    ]}
                    delay={0}
                />
                <Card
                    featured
                    badge="Partner Ravaglioli"
                    span="lg:col-span-6"
                    target="autoriparatore"
                    testid="service-card-autoriparatore"
                    icon={Wrench}
                    title="Autoriparatore: officina, gommista, carrozzeria"
                    items={[
                        "Ponti sollevatori e colonne mobili wireless Ravaglioli",
                        "Smontagomme, equilibratrici e assetto ruote 3D",
                        "Carrozzeria: forni di verniciatura, piani aspiranti e banchi di tiro",
                        "Linee revisione certificate auto, moto e mezzi pesanti",
                        "Utensileria professionale, arredo officina Beta, compressori e idropulitrici",
                        "Aspirazione gas di scarico e gestione computerizzata dell'olio",
                    ]}
                    delay={0.08}
                />
                <Card
                    span="lg:col-span-4"
                    testid="service-card-scaffalature"
                    icon={Warehouse}
                    title="Scaffalature Industriali"
                    items={[
                        "Scaffalature metalliche e portapallet",
                        "Progettazione e montaggio scaffalature",
                        "Installazione e verifiche di sicurezza",
                    ]}
                    delay={0.05}
                />
                <Card
                    span="lg:col-span-4"
                    testid="service-card-acque"
                    icon={Droplets}
                    title="Depurazione Acque"
                    items={[
                        "Trattamento acque in ingresso e in uscita",
                        "Impianti civili e industriali",
                        "Riciclo acqua per autolavaggi",
                        "Impianti a osmosi inversa e riciclo acque autolavaggio",
                    ]}
                    delay={0.12}
                />
                <Card
                    span="lg:col-span-4"
                    testid="service-card-coperture"
                    icon={Umbrella}
                    title="Coperture Esterne Christ"
                    items={[
                        "Strutture di copertura per autolavaggi",
                        "Pensiline e coperture per piazzali",
                        "Arredo esterno professionale",
                    ]}
                    delay={0.19}
                />
                <Card
                    span="lg:col-span-12"
                    testid="service-card-industriali"
                    icon={Factory}
                    title="Impianti Industriali"
                    items={[
                        "Realizzazione impianti di aria compressa industriali",
                        "Gestione automatizzata e computerizzata dell'olio nuovo ed esausto per concessionarie e officine",
                        "Realizzazione impianti di aspirazione gas di scarico per officine e industrie",
                        "Assemblaggio, installazione e assistenza di impianti industriali",
                        "Controlli periodici e manutenzione programmata",
                    ]}
                    delay={0.1}
                />
            </div>
        </div>
    </section>
);
